// Version: 3.8
// v3.8: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
// v3.7: Sidebar shows the user's name when set (email otherwise); the version
//       sits on its own line under the user block, so the avatar initial lines
//       up with the name and role again.
// v3.6: The Clearview version (the portal API's, from /api/health) shows in
//       faint text under the signed-in user.
// v3.5: Roles added under Admin. A 'staff' user (custom role) gets a nav built
//       from their permissions — the Sales section only, for now.
// v3.1: Fetch client features from API; filter CLIENT_NAV items by hub flag.
//       Add Subscription Tiers to admin nav. Hub field on each client nav item.
// v3.2: Align hub icons with stg.limited/clearview website:
//       ti-shield-half → ti-shield-check (Cyber Security Hub)
//       ti-rosette → ti-clipboard-check (Compliance Hub)
//       ti-brand-windows → ti-layout-grid (Productivity Hub)
//       ti-box → ti-archive (Assets Hub + admin Asset Register)
// v3.3: New Sales group in the admin nav: Prospect Search (/sales/search) and
//       Pipeline (/sales/pipeline). Admin only, replaces Clearscope Prospects.
// v3.4: Assessments group renamed Reports and Assessments; New Assessment is
//       now New Report or Assessment (the page offers all three outputs).
import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, authFetch } from './auth';
import './Shell.css';

const DEFAULT_NAV_LABELS = {
  dashboard:       'Dashboard',
  tickets:         'Tickets',
  cyber_security:  'Cyber Security Hub',
  domains:         'Digital Hub',
  devices:         'Device Hub',
  assets:          'Assets Hub',
  compliance:      'Compliance Hub',
  productivity:    'Productivity Hub',
  schedules:       'Schedules',
  resources:       'Resources',
  reports:         'Reports',
};

export default function Shell({ children }) {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const location         = useLocation();

  // ── as=client mode ──────────────────────────────────────────────────────────
  const [asClient, setAsClient] = useState(() => {
    const param = new URLSearchParams(window.location.search).get('as') === 'client';
    if (param) { sessionStorage.setItem('stg_as_client', '1'); return true; }
    return sessionStorage.getItem('stg_as_client') === '1';
  });

  useEffect(() => {
    const param = new URLSearchParams(location.search).get('as') === 'client';
    if (param) {
      sessionStorage.setItem('stg_as_client', '1');
      setAsClient(true);
      return;
    }
    if (!location.pathname.startsWith('/clients/') && asClient) {
      sessionStorage.removeItem('stg_as_client');
      setAsClient(false);
    }
  }, [location]);

  const exitClientView = useCallback(() => {
    sessionStorage.removeItem('stg_as_client');
    setAsClient(false);
    navigate('/manage');
  }, [navigate]);

  // ── State ──────────────────────────────────────────────────────────────────
  const [sidebarOpen,       setSidebarOpen]       = useState(false);
  const [activeCount,       setActiveCount]       = useState(0);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [productName,       setProductName]       = useState('');
  const [navLabels,         setNavLabels]         = useState(DEFAULT_NAV_LABELS);
  const [lastUpdated,       setLastUpdated]       = useState(null);
  const [features,          setFeatures]          = useState({});
  const [appVersion,        setAppVersion]        = useState('');
  const [userName,          setUserName]          = useState(user?.name || '');

  // v3.7: the user's name, when set on Users (also for sessions from before it existed)
  useEffect(() => {
    if (!user?.username) return;
    authFetch('/api/auth/me').then(r => r.ok ? r.json() : null).then(d => { if (d) setUserName(d.name || ''); }).catch(() => {});
  }, [user?.username]);

  // Product name
  useEffect(() => {
    fetch('/api/health')
      .then(r => r.json())
      .then(d => {
        const name = d.product || '';
        setProductName(name);
        if (d.version && d.version !== 'unknown') setAppVersion(d.version);
        if (name) document.title = `STG ${name}`;
      })
      .catch(() => {});
  }, []);

  const isAdmin = user?.role === 'admin' && !asClient;

  // Active scan count (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    const run = () => {
      authFetch('/api/assessments/active')
        .then(r => r.json())
        .then(d => setActiveCount(Array.isArray(d) ? d.length : 0))
        .catch(() => {});
    };
    run();
    const interval = setInterval(run, 15000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  // Slug from URL
  const urlSlug = location.pathname.match(/^\/clients\/([^/]+)/)?.[1] || null;
  const slug    = urlSlug || user?.client_slug;

  // Nav label overrides — load from dashboard config per client
  useEffect(() => {
    if (!slug) return;
    authFetch(`/api/clients/${slug}/dashboard-config`)
      .then(r => r.json())
      .then(d => {
        if (d?.nav_labels) setNavLabels({ ...DEFAULT_NAV_LABELS, ...d.nav_labels });
        if (d?.last_updated) setLastUpdated(d.last_updated);
      })
      .catch(() => {});
  }, [slug]);

  // Features — only needed in client context
  useEffect(() => {
    if (!slug || isAdmin) return;
    authFetch(`/api/clients/${slug}/features`)
      .then(r => r.json())
      .then(d => setFeatures(d?.features || {}))
      .catch(() => {});
  }, [slug, isAdmin]);

  // Last updated timestamp
  useEffect(() => {
    setLastUpdated(new Date().toISOString());
  }, [slug]);

  const closeSidebar = () => setSidebarOpen(false);

  // Hub visibility helper for client nav
  const hubVisible = (hub) => !hub || features[hub];

  // ── Current page title for topbar ─────────────────────────────────────────
  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes('/tickets'))        return navLabels.tickets;
    if (path.includes('/cyber-security')) return navLabels.cyber_security;
    if (path.includes('/domains'))        return navLabels.domains;
    if (path.includes('/devices'))        return navLabels.devices;
    if (path.includes('/assets'))         return navLabels.assets;
    if (path.includes('/compliance'))     return navLabels.compliance;
    if (path.includes('/productivity'))   return navLabels.productivity;
    if (path.includes('/schedules'))      return navLabels.schedules;
    if (path.includes('/resources'))      return navLabels.resources;
    if (path.includes('/reports'))        return navLabels.reports;
    return navLabels.dashboard;
  };

  const formatTopbarDate = () => {
    return new Date().toLocaleString('en-GB', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
      timeZone: 'Europe/London',
    }) + ' GMT';
  };

  // ── Admin nav ──────────────────────────────────────────────────────────────
  const ADMIN_NAV = [
    {
      group: 'Overview',
      items: [
        { to: '/dashboard',      label: 'Dashboard',           icon: 'ti-layout-dashboard' },
        { to: '/manage',         label: 'Client Management',   icon: 'ti-users' },
        { to: '/activity',       label: 'Activity Log',        icon: 'ti-activity' },
        { to: '/audit',          label: 'Audit Log',           icon: 'ti-clipboard-list' },
      ],
    },
    {
      group: 'Reports and Assessments',
      items: [
        { to: '/new',            label: 'New Report or Assessment', icon: 'ti-plus' },
        { to: '/active',         label: 'Active Scans',        icon: 'ti-radar', badge: activeCount },
      ],
    },
    {
      group: 'Infrastructure',
      items: [
        { to: '/edge-nodes',     label: 'Edge Nodes',          icon: 'ti-server' },
        { to: '/agent-nodes',    label: 'Agent Nodes',         icon: 'ti-cpu' },
        { to: '/asset-register', label: 'Asset Register',      icon: 'ti-archive' },
        { to: '/saas',           label: 'SaaS Discovery',      icon: 'ti-topology-star' },
        { to: '/network',        label: 'Network Devices',     icon: 'ti-router' },
      ],
    },
    {
      group: 'Sales',
      items: [
        { to: '/sales/search',   label: 'Prospect Search',     icon: 'ti-building-store' },
        { to: '/sales/pipeline', label: 'Pipeline',            icon: 'ti-layout-kanban' },
      ],
    },
    {
      group: 'Tools',
      items: [
        { to: '/tickets',        label: 'Tickets',             icon: 'ti-ticket' },
        { to: '/schedules',      label: 'Schedules',           icon: 'ti-calendar' },
        { to: '/resources',      label: 'Resources',           icon: 'ti-books' },
        { to: '/pack-editor',    label: 'Pack Editor',         icon: 'ti-package' },
      ],
    },
    {
      group: 'Admin',
      items: [
        { to: '/users',                label: 'Users',               icon: 'ti-user-cog' },
        { to: '/roles',                label: 'Roles',               icon: 'ti-lock-cog' },
        { to: '/subscription-tiers',   label: 'Subscription Tiers',  icon: 'ti-credit-card' },
        { to: '/admin',                label: 'System Admin',         icon: 'ti-settings' },
      ],
    },
  ];

  // ── Client nav — filtered by hub feature flags ─────────────────────────────
  const rawClientGroups = slug ? [
    {
      group: 'Overview',
      items: [
        { to: `/clients/${slug}`,                label: navLabels.dashboard, icon: 'ti-layout-dashboard', exact: true, hub: null },
        { to: `/clients/${slug}/reports`,        label: navLabels.reports,   icon: 'ti-trending-up',      hub: null },
      ],
    },
    {
      group: 'Monitoring',
      items: [
        { to: `/clients/${slug}/devices`,        label: navLabels.devices,        icon: 'ti-device-laptop',    hub: 'hub_device' },
        { to: `/clients/${slug}/cyber-security`, label: navLabels.cyber_security, icon: 'ti-shield-check',     hub: 'hub_cyber' },
        { to: `/clients/${slug}/compliance`,     label: navLabels.compliance,     icon: 'ti-clipboard-check',  hub: 'hub_compliance' },
        { to: `/clients/${slug}/productivity`,   label: navLabels.productivity,   icon: 'ti-layout-grid',      hub: 'hub_productivity' },
        { to: `/clients/${slug}/domains`,        label: navLabels.domains,        icon: 'ti-world',            hub: 'hub_digital' },
        { to: `/clients/${slug}/assets`,         label: navLabels.assets,         icon: 'ti-archive',          hub: 'hub_assets' },
      ],
    },
    {
      group: 'Tools',
      items: [
        { to: `/clients/${slug}/tickets`,        label: navLabels.tickets,   icon: 'ti-ticket',    hub: null },
        { to: `/clients/${slug}/schedules`,      label: navLabels.schedules, icon: 'ti-calendar',  hub: null },
        { to: `/clients/${slug}/resources`,      label: navLabels.resources, icon: 'ti-books',     hub: null },
      ],
    },
  ] : [];

  const CLIENT_NAV = rawClientGroups
    .map(group => ({ ...group, items: group.items.filter(item => hubVisible(item.hub)) }))
    .filter(group => group.items.length > 0);

  // Staff (custom-role) nav — built from the user's permissions. Sales only for now.
  const perms = new Set(user?.permissions || []);
  const STAFF_NAV = (() => {
    const sales = [];
    if (perms.has('sales.view')) sales.push({ to: '/sales/pipeline', label: 'Pipeline', icon: 'ti-layout-kanban' });
    if (perms.has('sales.search')) sales.push({ to: '/sales/search', label: 'Prospect Search', icon: 'ti-building-store' });
    const groups = [];
    if (sales.length) groups.push({ group: 'Sales', items: sales });
    return groups;
  })();

  const navGroups = isAdmin ? ADMIN_NAV : (user?.role === 'staff' ? STAFF_NAV : CLIENT_NAV);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="shell">
      <div className={`sidebar-overlay${sidebarOpen ? ' open' : ''}`} onClick={closeSidebar} />

      <aside className={`sidebar${sidebarOpen ? ' open' : ''}`}>

        {/* Brand */}
        <div className="sidebar-logo">
          <div className="sidebar-brand-row">
            <div className="sidebar-brand-icon">
              <i className="ti ti-shield-check" />
            </div>
            <div className="sidebar-brand-text">
              <div className="sidebar-brand-name">
                {productName || 'Clearview'}
              </div>
              <div className="sidebar-brand-sub">Swanswater Technology Group</div>
            </div>
          </div>
        </div>

        {/* Client name strip (client view only) */}
        {!isAdmin && slug && (
          <div className="sidebar-client-selector">
            <div className="sidebar-client-label">Viewing</div>
            <div className="sidebar-client-name">
              {slug.replace(/_/g, ' ')}
            </div>
          </div>
        )}

        {/* Nav */}
        <nav className="sidebar-nav">
          {navGroups.map(group => (
            <React.Fragment key={group.group}>
              <div className="nav-group-label">{group.group}</div>
              {group.items.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.exact}
                  className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                  onClick={closeSidebar}
                >
                  <i className={`ti ${item.icon}`} />
                  <span className="nav-label">{item.label}</span>
                  {item.badge > 0 && (
                    <span className="nav-badge">{item.badge}</span>
                  )}
                </NavLink>
              ))}
            </React.Fragment>
          ))}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer" style={{flexWrap:'wrap'}}>
          <div className="user-info">
            <div className="user-avatar">
              {(userName || user?.username || '?')[0].toUpperCase()}
            </div>
            <div className="user-meta">
              <div className="user-name" title={user?.username}>{userName || user?.username}</div>
              <div className="user-role">{user?.role}</div>
            </div>
          </div>
          <button
            className="logout-btn"
            onClick={() => setShowLogoutConfirm(true)}
            title="Sign out"
          >
            <i className="ti ti-logout" />
          </button>
          {appVersion && <div className="app-version" title="Clearview version (portal API)" style={{flexBasis:'100%',paddingLeft:37,fontSize:9,color:'rgba(255,255,255,0.18)',fontFamily:'var(--font-mono)',marginTop:-4}}>Clearview v{appVersion}</div>}
        </div>
      </aside>

      {/* Main */}
      <main className="main-content">

        {/* Mobile topbar */}
        <div className="mobile-topbar">
          <div className="mobile-logo">
            <div className="mobile-logo-icon">
              <i className="ti ti-shield-check" />
            </div>
            <span className="mobile-logo-name">{productName || 'Clearview'}</span>
          </div>
          <button className="hamburger" onClick={() => setSidebarOpen(o => !o)}>
            <i className={`ti ${sidebarOpen ? 'ti-x' : 'ti-menu-2'}`} />
          </button>
        </div>

        {/* Client topbar (desktop — client view only) */}
        {!isAdmin && slug && (
          <div className="shell-topbar">
            <div className="shell-topbar-left">
              <div className="shell-topbar-title">{getPageTitle()}</div>
              <div className="shell-topbar-sub">Updated {formatTopbarDate()}</div>
            </div>
            <div className="shell-topbar-right">
              <div className="shell-status-pill green">
                <i className="ti ti-circle-filled" />
                All systems active
              </div>
              <div className="shell-topbar-avatar">
                {user?.username?.[0]?.toUpperCase()}
              </div>
            </div>
          </div>
        )}

        {/* As-client warning banner */}
        {asClient && user?.role === 'admin' && (
          <div className="as-client-banner">
            <i className="ti ti-eye" />
            <span>Viewing as client — this is what {slug?.replace(/_/g, ' ')} sees</span>
            <button className="btn btn-quiet" onClick={exitClientView}>Exit client view</button>
          </div>
        )}

        <div className="main-inner">{children}</div>
      </main>

      {/* Logout confirm */}
      {showLogoutConfirm && (
        <div className="modal-overlay" onClick={() => setShowLogoutConfirm(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Sign out?</div>
            <div className="modal-body">
              You will need to sign in again to access the portal.
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary"
               
                onClick={async () => { await logout(); navigate('/login'); }}
              >
                Sign out
              </button>
              <button className="btn btn-secondary" onClick={() => setShowLogoutConfirm(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
